import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiCall } from '../utils/api';
import { toast } from './ui/sonner';
import { useWebSocket } from '../contexts/WebSocketContext';
import { Send, X, MessageSquare, Wifi, WifiOff, User } from 'lucide-react';

export default function RealTimeChat({ recipientId, recipientName, jobId, onClose }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const { isConnected, sendMessage, lastMessage } = useWebSocket();

  const currentUserId = JSON.parse(localStorage.getItem('user'))?._id;
  const conversationId = getConversationId(currentUserId, recipientId, jobId);

  function getConversationId(userId1, userId2, jobId) {
    const sortedIds = [userId1, userId2].sort();
    let convId = `${sortedIds[0]}_${sortedIds[1]}`;
    if (jobId) convId = `${convId}_${jobId}`;
    return convId;
  }

  // Load initial messages
  useEffect(() => {
    loadMessages();
  }, [recipientId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle incoming WebSocket messages
  useEffect(() => {
    if (lastMessage) {
      if (lastMessage.type === 'new_message' && lastMessage.message.sender_id === recipientId) {
        setMessages(prev => [...prev, lastMessage.message]);
        setIsTyping(false);
      } else if (lastMessage.type === 'typing' && lastMessage.sender_id === recipientId) {
        setIsTyping(true);
      } else if (lastMessage.type === 'stop_typing' && lastMessage.sender_id === recipientId) {
        setIsTyping(false);
      }
    }
  }, [lastMessage, recipientId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async () => {
    if (!recipientId) return;
    
    setLoading(true);
    try {
      const response = await apiCall(`/api/messages/${conversationId}`);
      setMessages(response.messages || []);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTyping = useCallback(() => {
    if (isConnected) {
      sendMessage({
        type: 'typing',
        recipient_id: recipientId
      });
      
      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Set timeout to send stop_typing
      typingTimeoutRef.current = setTimeout(() => {
        sendMessage({
          type: 'stop_typing',
          recipient_id: recipientId
        });
      }, 2000);
    }
  }, [isConnected, sendMessage, recipientId]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const messageContent = newMessage.trim();
    setNewMessage('');
    setSending(true);

    // Clear typing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    try {
      if (isConnected) {
        // Send via WebSocket for real-time delivery
        sendMessage({
          type: 'chat_message',
          recipient_id: recipientId,
          content: messageContent,
          job_id: jobId
        });
        
        // Optimistically add message to UI
        const optimisticMessage = {
          _id: `temp-${Date.now()}`,
          sender_id: currentUserId,
          recipient_id: recipientId,
          content: messageContent,
          created_at: new Date().toISOString(),
          read: false
        };
        setMessages(prev => [...prev, optimisticMessage]);
      } else {
        // Fallback to HTTP
        await apiCall('/api/messages', {
          method: 'POST',
          body: JSON.stringify({
            recipient_id: recipientId,
            job_id: jobId,
            message: messageContent,
          }),
        });
        loadMessages();
      }
    } catch (error) {
      toast.error('Failed to send message');
      setNewMessage(messageContent); // Restore message
    } finally {
      setSending(false);
    }
  };

  // Cleanup typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="fixed bottom-4 right-4 w-96 bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col h-[520px] z-50" data-testid="realtime-chat-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-2xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">{recipientName}</h3>
            <div className="flex items-center gap-1.5">
              {isConnected ? (
                <>
                  <Wifi className="w-3 h-3 text-emerald-300" />
                  <span className="text-xs text-emerald-300">Live</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-3 h-3 text-amber-300" />
                  <span className="text-xs text-amber-300">Offline</span>
                </>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-white/20 rounded-full transition"
          data-testid="close-chat-btn"
        >
          <X className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-400">
            <MessageSquare className="w-12 h-12 mb-2 opacity-50" />
            <p className="text-sm">No messages yet</p>
            <p className="text-xs">Start the conversation!</p>
          </div>
        ) : (
          <>
            {messages.map((msg, index) => {
              const isSent = msg.sender_id === currentUserId;
              return (
                <div
                  key={msg._id || index}
                  className={`flex ${isSent ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] px-4 py-2.5 rounded-2xl ${
                      isSent
                        ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-br-md'
                        : 'bg-white border border-slate-200 text-slate-800 rounded-bl-md shadow-sm'
                    }`}
                  >
                    <p className="text-sm leading-relaxed">{msg.content || msg.message}</p>
                    <p className={`text-xs mt-1 ${isSent ? 'text-blue-200' : 'text-slate-400'}`}>
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })}
            
            {/* Typing indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <form onSubmit={handleSendMessage} className="p-3 border-t border-slate-200 bg-white rounded-b-2xl">
        <div className="flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value);
              handleTyping();
            }}
            placeholder="Type a message..."
            className="flex-1 px-4 py-2.5 bg-slate-100 border-0 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white transition"
            data-testid="chat-input"
          />
          <button
            type="submit"
            disabled={sending || !newMessage.trim()}
            className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
            data-testid="send-message-btn"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </form>
    </div>
  );
}
